import React, { useCallback, useEffect, useState } from 'react';
import Table from './subcomponents/Table';
import { TableWithHeader } from 'dyl-components';
import Toolbar from './subcomponents/Toolbar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BulkActionsProvider from 'shared/context/BulkActionsProvider';
import { useDispatch, useSelector } from 'react-redux';
import pbxConfigActions from 'actions/pbx_config';
import productsActions from 'actions/products';
import EditDylNumberModal from 'shared/modals/EditDylNumberModal/EditDylNumberModal';
import NewNumberPopup from './subcomponents/NewNumberPopup';
import usersActions from 'actions/users';

const DylNumbers = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const dispatch = useDispatch();
    const searchQuery = params.get('search') || '';
    const [search, setSearch] = useState(searchQuery);
    const [openModal, setOpenModal] = useState(null);
    const { dylNumbersCount, isReadingDylNumbers, dylNumbers } = useSelector((state) => state.pbx_config);
    const [newNumberType, setNewNumberType] = useState('');
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');

    const onChangeSearch = (_, { value }) => {
        setSearch(value);
    }

    const cancelFunction = () => {
        setSearch("");
        const query = new URLSearchParams(params);
        query.delete('search');
        const query_string = query.toString();
        navigate(`/settings/phone-management/numbers/dyl-numbers${query_string ? `?${query_string}` : ''}`,);
    }

    const onSearchSubmit = (value) => {
        const query = new URLSearchParams(params);
        query.set('search', value.trim());
        query.set('page', 1);
        const query_string = query.toString();
        navigate(`/settings/phone-management/numbers/dyl-numbers${query_string ? `?${query_string}` : ''}`);
    }

    const onPageChange = (_, { activePage }) => {
        const query = new URLSearchParams(params);
        query.set('page', activePage);
        const query_string = query.toString();
        navigate(`/settings/phone-management/numbers/dyl-numbers${query_string ? `?${query_string}` : ''}`);
    }
    const onNewNumberClose = () => {
        setOpenModal(null);
        setSelectedSubscriptionId(null)
    }

    const [filters, setFilters] = useState({
        type: params.get('type')?.split(','),
        destination_type: params.get('destination_type')?.split(',')
    })

    const onFilter = async (_, { name, value }) => {
        setFilters({
            ...filters,
            [name]: value
        });
    }

    const onCloseEditDylNumberModal = () => {
        setOpenModal(null)
    }

    const onOpenEditDylNumberModal = async (number) => {
        dispatch(pbxConfigActions.getDylNumber(number));
        dispatch(usersActions.readUserOptions({ limit: 500, page: 1 }))
        setOpenModal("edit-number");
    }
    const onNewNumber = async (type, subscription_id) => {
        setNewNumberType(type);
        setSelectedSubscriptionId(subscription_id)
        setOpenModal("add-number")
    }

    const onReload = useCallback(async () => {
        const queryParameters = { ...Object.fromEntries(params || []) };

        let subscriptionSlotQueryParams = {};
        let dylNumberQueryParams = {};

        const type = queryParameters?.type;

        if (type) {
            dylNumberQueryParams = { type };

            switch (type) {
                case "tollfree":
                    subscriptionSlotQueryParams = {
                        phone_number_type: ["toll_free_number", "port_in_toll_free_number"].join(","),
                    };
                    break;

                case "local":
                    subscriptionSlotQueryParams = {
                        phone_number_type: ["local_number", "port_in_local_number"].join(","),
                    };
                    break;

                default:
                    subscriptionSlotQueryParams = {};
                    break;
            }
        }
        
        dispatch(productsActions.getOpenSubscriptionSlots(subscriptionSlotQueryParams));
        dispatch(pbxConfigActions.getDylNumbers(dylNumberQueryParams));
    }, [dispatch, params]);


    useEffect(() => {
        const query = new URLSearchParams(params);
        if (search.trim()) {
            query.set('search', search);
        } else {
            query.delete('search');
        }
        const { type, destination_type } = filters;
        if (type?.length > 0) {
            query.set('type', type.join(','));
        } else {
            query.delete('type');
        }
        if (destination_type?.length > 0) {
            query.set('destination_type', destination_type.join(','));
        } else {
            query.delete('destination_type');
        }
        const query_string = query.toString();
        navigate(`/settings/phone-management/numbers/dyl-numbers${query_string ? `?${query_string}` : ''}`,);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    useEffect(() => {
        onReload();
    }, [onReload])

    const openSubscriptionSlots = useSelector((state) => state.products.openSubscriptionSlots?.map(slot => ({
        ...slot,
        type: slot.phone_number_type
    })) || []);
    const LIMIT = 25;

    const activePage = parseInt(params.get('page') || '1', 10);
    const dylNumbersTotalPages = Math.ceil(dylNumbersCount / LIMIT);
    const searchFilter = params.get('search');
    const destinationTypeFilter = params.get('destination_type');
    const openSubscriptionSlotsToDisplay = !searchFilter && !destinationTypeFilter ? openSubscriptionSlots : [];
    const totalCount = dylNumbersCount + openSubscriptionSlotsToDisplay.length;
    const totalPages = Math.ceil(totalCount / LIMIT);
    const toDisplay = (() => {
        if(destinationTypeFilter === "none"){
            const start = 0
            return openSubscriptionSlots.slice(start, start + LIMIT)?.map((ele)=>{
                return {
                    ...ele,
                    isEditDisable: true,
                }});
        }
        if (activePage < dylNumbersTotalPages) {
            return dylNumbers;
        }
        if (activePage === dylNumbersTotalPages) {
            const remaining = LIMIT - (dylNumbers.length % LIMIT || LIMIT);
            return [...dylNumbers, ...openSubscriptionSlotsToDisplay.slice(0, remaining)?.map((ele) => ({
                ...ele,
                isEditDisable: true,
            }))];
        }
        if (activePage <= totalPages) {
            const start = (activePage - dylNumbersTotalPages - 1) * LIMIT;
            return openSubscriptionSlots.slice(start, start + LIMIT)?.map((ele)=>{
                return {
                    ...ele,
                    isEditDisable: true,
                }});
        }
        return [];
    })();

    return <>
        <div className='DylNumbers'>
            <TableWithHeader
                header={(
                    <Toolbar
                        search={searchQuery}
                        onChangeSearch={onChangeSearch}
                        onSearchSubmit={onSearchSubmit}
                        cancelFunction={cancelFunction}
                    />
                )}
                table={
                    <Table
                        onFilter={onFilter}
                        onPageChange={onPageChange}
                        onOpenEditDylNumberModal={onOpenEditDylNumberModal}
                        dylNumbers={toDisplay}
                        dylNumbersCount={totalCount}
                        isReadingDylNumbers={isReadingDylNumbers}
                        onNewNumber={onNewNumber}
                    />
                }
            />
        </div>
        <EditDylNumberModal open={openModal === "edit-number"} onClose={onCloseEditDylNumberModal} onReload={onReload} />
        <NewNumberPopup type={newNumberType} open={openModal === "add-number"} onClose={onNewNumberClose} onReload={onReload} subscription_id={selectedSubscriptionId} />
    </>
}

export default () => (
    <BulkActionsProvider>
        <DylNumbers />
    </BulkActionsProvider>
);
